import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button, RadioButton, RadioButtonGroup } from '@carbon/react';
import { Close as CloseIcon } from '@carbon/react/icons';
import { activeTcWorkspaceAtom } from 'components/type-curve-workspace/atoms';
import { MineralType } from 'types/minerals';
import type {
  DcaScenariosResponse,
  DcaForecastParameter,
} from 'types/api-responses';
import type { TypeCurveProbitResponse } from 'types/type-curve-probit';
import {
  selectedDcaScenarioAtom,
  dcaScenariosAtom,
} from 'components/well-page-content/atoms';
import { Toggle } from 'components/toggle/toggle';
import type { DiFormat, QiFormat } from 'types/api-payloads';
import type { FilteredWell } from '../types';
import {
  useProductionVolumes,
  useTypeCurveVolumes,
  useAggregationVolumes,
} from '../hooks';
import {
  tcStreamAggregationAtom,
  tcProductionWellsAtom,
  tcTypeCurveVolumesAtom,
  tcAggregationCacheAtom,
  tcWellCountsAtom,
  tcForecastOverrideAtom,
  probitLastDispatchKey,
  tcProbitOverrideAtoms,
} from '../atoms';
import { tcHasTypeCurveAreaAtom } from '../type-curve-filters/atoms';
import { TcDcaFormProvider, useTcDcaForm } from './tc-dca-form-context';
import { TcDcaConfigurationPanel } from './tc-dca-configuration-panel';
import { TcDcaChartGrid } from './tc-dca-chart-grid';
import {
  useTcDcaRegression,
  buildFormDcaParams,
} from './use-tc-dca-regression';
import { useForecastVolumes } from './use-forecast-volumes';
import {
  useAreaConfigPrefill,
  mapProductionStreamConfigs,
  mapDcaForecastParamsToStreamParams,
  extractInitialLockStates,
} from './use-area-config-prefill';
import { useTypeCurveHeaders } from './use-type-curve-headers';
import { DEFAULT_STREAM_DCA_PARAMS } from './types';
import {
  tcDcaForecastVolumesAtom,
  tcDcaFitParamsAtom,
  tcDcaFitSegmentsAtom,
  tcDcaFitAggregationAtom,
  tcDcaFitScenarioIdAtom,
} from './dca-fit-atoms';
import styles from './tc-dca-modal.module.scss';

const STREAM_LABELS: Record<MineralType, string> = {
  oil: 'Oil',
  gas: 'Gas',
  water: 'Water',
};

const AGG_TO_API: Record<string, string> = {
  arithmeticMean: 'arithmetic_mean',
  geometricMean: 'geometric_mean',
};
const toApiAggregation = (type: string) => AGG_TO_API[type] ?? type;

interface TcDcaModalProps {
  open: boolean;
  stream: MineralType | null;
  onClose: () => void;
  dcaScenarioId: number;
  wells: FilteredWell[];
  typeCurveAreaName: string;
  typeCurveHeaderDetailsId: number | null;
  dcaForecastParameters?: DcaForecastParameter[];
  /** Default aggregation type from typeWellDefaults — used as fallback instead of 'p50'. */
  defaultAggregation?: string;
  /** DCA scenario ID from typeWellDefaults — pre-selects the scenario when opening the modal. */
  typeWellDefaultDcaScenarioId?: number | null;
}

export const TcDcaModal = ({
  open,
  stream,
  onClose,
  dcaScenarioId,
  wells,
  typeCurveAreaName,
  typeCurveHeaderDetailsId,
  dcaForecastParameters,
  defaultAggregation,
  typeWellDefaultDcaScenarioId,
}: TcDcaModalProps) => {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open || !stream) return null;

  return (
    <TcDcaFormProvider>
      <TcDcaModalContent
        stream={stream}
        onClose={onClose}
        dcaScenarioId={dcaScenarioId}
        wells={wells}
        typeCurveAreaName={typeCurveAreaName}
        typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
        dcaForecastParameters={dcaForecastParameters}
        defaultAggregation={defaultAggregation}
        typeWellDefaultDcaScenarioId={typeWellDefaultDcaScenarioId}
      />
    </TcDcaFormProvider>
  );
};

// Inner component rendered inside TcDcaFormProvider so hooks can access the form context.
const TcDcaModalContent = ({
  stream,
  onClose,
  dcaScenarioId,
  wells,
  typeCurveAreaName,
  typeCurveHeaderDetailsId: initialTypeCurveHeaderDetailsId,
  dcaForecastParameters,
  defaultAggregation,
  typeWellDefaultDcaScenarioId,
}: Omit<TcDcaModalProps, 'open'> & { stream: MineralType }) => {
  // Active TC is driven entirely by the Type Curves accordion selection.
  // Initialised from the parent's typeCurveHeaderDetailsId so the modal opens
  // with the current type curve pre-selected.
  const [activeUniqueTcId, setActiveUniqueTcId] = useState<string>('');

  // The header detail the charts, fit and prefill run against. Seeded from the
  // prop the page already resolved, NOT from the Type Curves list: that list is
  // the promoted+collapsed `type_curves` view, so an unpromoted workspace clone is
  // absent from it and its uniqueTcId is null. Deriving the id from it left this
  // null on those workspaces and every request was skipped.
  const [selectedHeaderId, setSelectedHeaderId] = useState<number | null>(
    initialTypeCurveHeaderDetailsId ?? null
  );

  // Scenario for THIS modal only — fit, charts and save read it, and changing it
  // must not move the page's DCA Scenario. Seeded from the page's selection on
  // open (this component unmounts on close, so every open re-seeds).
  // When typeWellDefaultDcaScenarioId is set, prefer that over the page scenario.
  const pageDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const dcaScenariosState = useAtomValue(dcaScenariosAtom);
  const [modalDcaScenario, setModalDcaScenario] =
    useState<DcaScenariosResponse | null>(null);

  const typeWellDefaultScenario = useMemo(() => {
    if (!typeWellDefaultDcaScenarioId) return null;
    const results =
      dcaScenariosState.result?.type === 'success'
        ? dcaScenariosState.result.data.results
        : [];
    return results.find((s) => s.id === typeWellDefaultDcaScenarioId) ?? null;
  }, [typeWellDefaultDcaScenarioId, dcaScenariosState.result]);

  useEffect(() => {
    if (!modalDcaScenario) {
      const seed = typeWellDefaultScenario ?? pageDcaScenario;
      if (seed) setModalDcaScenario(seed);
    }
  }, [modalDcaScenario, typeWellDefaultScenario, pageDcaScenario]);

  const effectiveDcaScenarioId = modalDcaScenario?.id ?? dcaScenarioId;

  // Snapshot the shared probit dispatch key on mount. If the user cancels
  // (closes without saving), the cleanup restores it so the main page's dedup
  // check still matches its original dispatch — preventing a spurious re-fetch
  // or stale key from the modal's intermediate fits.
  const probitKeySnapshotRef = useRef(probitLastDispatchKey[stream]);
  const wasSavedRef = useRef(false);
  useEffect(() => {
    const snapshotKey = probitKeySnapshotRef.current;
    return () => {
      if (!wasSavedRef.current) {
        probitLastDispatchKey[stream] = snapshotKey;
      }
    };
  }, [stream]);

  // Prevent page scrolling while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const streamAggTypes = useAtomValue(tcStreamAggregationAtom);
  const aggregation = streamAggTypes[stream] ?? (defaultAggregation || 'p50');

  const uwis = useMemo(() => wells.map((w) => w.uwi), [wells]);

  // ── Shared atom snapshots (main page data) ──
  const sharedProductionWells = useAtomValue(tcProductionWellsAtom);
  const sharedTypeCurveVolumes = useAtomValue(tcTypeCurveVolumesAtom);
  const sharedAggregationCache = useAtomValue(tcAggregationCacheAtom);
  const sharedWellCounts = useAtomValue(tcWellCountsAtom);

  // ── Conditional fetching: only fetch when TC or scenario diverge from main page ──
  const isContextDivergent =
    selectedHeaderId !== initialTypeCurveHeaderDetailsId ||
    effectiveDcaScenarioId !== dcaScenarioId;

  // Hooks always called (rules-of-hooks), but pass null/empty params when not
  // divergent so they short-circuit and make zero API calls.
  const fetchHeaderId = isContextDivergent ? selectedHeaderId : null;
  const fetchWells = isContextDivergent ? wells : [];

  const { wells: fetchedProductionWells, isLoading: fetchedProductionLoading } =
    useProductionVolumes({
      filteredUwis: fetchWells,
      typeCurveHeaderDetailsId: fetchHeaderId,
      dcaScenarioId: effectiveDcaScenarioId,
    });

  const hasTypeCurveArea = useAtomValue(tcHasTypeCurveAreaAtom) !== false;
  const { typeCurveData: fetchedTypeCurveData } = useTypeCurveVolumes({
    typeCurveHeaderDetailsId: fetchHeaderId,
    dcaScenarioId: effectiveDcaScenarioId,
    hasTypeCurveArea,
  });

  const requestedAggTypes = useMemo(() => [aggregation], [aggregation]);
  const fetchedUwis = isContextDivergent ? uwis : [];
  const {
    aggregationCache: fetchedAggregationCache,
    wellCounts: fetchedWellCounts,
  } = useAggregationVolumes({
    filteredUwis: fetchedUwis,
    typeCurveHeaderDetailsId: fetchHeaderId,
    dcaScenarioId: effectiveDcaScenarioId,
    requestedTypes: requestedAggTypes,
  });

  // Resolve: use fetched data when divergent, shared atoms otherwise
  const effectiveProductionWells = isContextDivergent
    ? fetchedProductionWells
    : sharedProductionWells;
  const effectiveProductionLoading = isContextDivergent
    ? fetchedProductionLoading
    : false;
  const effectiveTypeCurveData = isContextDivergent
    ? fetchedTypeCurveData
    : sharedTypeCurveVolumes;
  const effectiveAggregationCache = isContextDivergent
    ? fetchedAggregationCache
    : sharedAggregationCache;
  const effectiveWellCounts = isContextDivergent
    ? fetchedWellCounts
    : sharedWellCounts;

  // Chart controls state.
  const [showTypeCurve, setShowTypeCurve] = useState(true);
  const [volumeDisplayMode, setVolumeDisplayMode] = useState<
    'monthly' | 'daily'
  >('monthly');

  const { form, isFormDirty, isFormValid, setAllLockStates } = useTcDcaForm();

  // DCA parameters are editable only inside an active, unpromoted workspace.
  // A promoted workspace — or viewing a promoted/canonical Type Curve with no
  // active workspace — is read-only (inputs + Fit disabled via the fieldset).
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const savedFitParams = useAtomValue(tcDcaFitParamsAtom);
  const isPromoted = !!activeWorkspace?.promoted;
  const isReadOnly = !activeWorkspace || isPromoted;

  // -------------------------------------------------------------------------
  // Area config pre-fill: resolve activeUniqueTcId → numeric id, then fetch
  // production stream configs and populate form fields.
  // -------------------------------------------------------------------------
  const { data: headersData } = useTypeCurveHeaders(typeCurveAreaName);

  // Seed the active TC from the parent's numeric typeCurveHeaderDetailsId once
  // the headers list loads. Only runs when activeUniqueTcId hasn't been set yet.
  useEffect(() => {
    if (
      activeUniqueTcId ||
      !headersData?.results ||
      initialTypeCurveHeaderDetailsId === null ||
      initialTypeCurveHeaderDetailsId === undefined
    )
      return;
    const match = headersData.results.find(
      (h) => h.id === initialTypeCurveHeaderDetailsId
    );
    if (match?.uniqueTcId) setActiveUniqueTcId(match.uniqueTcId);
  }, [activeUniqueTcId, headersData, initialTypeCurveHeaderDetailsId]);

  const { fit, isPending, isError, error, data, fitResults } =
    useTcDcaRegression({
      uwis,
      typeCurveHeaderDetailsId: selectedHeaderId,
      dcaScenarioId: effectiveDcaScenarioId,
      aggregation: toApiAggregation(aggregation),
      stream,
    });

  const regressionResult = data?.typeCurveDcaRegressionResult ?? null;

  const { data: prefillData } = useAreaConfigPrefill(
    effectiveDcaScenarioId,
    selectedHeaderId,
    true // always fetch — needed as fallback for streams without dcaForecastParameters
  );

  // -------------------------------------------------------------------------
  // Classify which streams have saved DCA forecast parameters.
  // Match by stream only — if the DCA parameter table has an entry for a
  // stream, use it (sticky). Streams without saved params fall through to
  // DCA scenario/area-config defaults which update when the scenario changes.
  // -------------------------------------------------------------------------
  const classifyStreams = useMemo(() => {
    const find = (s: MineralType) => {
      const atomParams = savedFitParams[s];
      if (atomParams) {
        return { stream: s, dcaParams: atomParams } as DcaForecastParameter;
      }
      return dcaForecastParameters?.find((p) => p.stream === s) ?? null;
    };

    return { oil: find('oil'), gas: find('gas'), water: find('water') };
  }, [dcaForecastParameters, savedFitParams]);

  const hasSavedParams = useMemo(
    () => ({
      oil: !!classifyStreams.oil,
      gas: !!classifyStreams.gas,
      water: !!classifyStreams.water,
    }),
    [classifyStreams]
  );

  // -------------------------------------------------------------------------
  // Saved-params prefill: use dcaForecastParameters for any workspace (not
  // just promoted). Runs once on modal open; changing DCA scenario does NOT
  // re-trigger — values are sticky to the initial scenario.
  // -------------------------------------------------------------------------
  const savedParamsPrefillDoneRef = useRef(false);

  useEffect(() => {
    if (savedParamsPrefillDoneRef.current) return;

    const { oil, gas, water } = classifyStreams;
    if (!oil && !gas && !water) return;

    // Forecast settings from any matched entry (all share format values).
    const anyParams = (oil ?? gas ?? water)!.dcaParams;
    form.setFieldValue(
      'qiFormat',
      (anyParams.qi_format ?? 'months') as QiFormat
    );
    form.setFieldValue('diFormat', anyParams.di_format as DiFormat);
    form.setFieldValue('forecastDurationPeriod', anyParams.duration_period);
    // durationOption is NOT in DcaForecastParams — set from area config below.

    if (oil) {
      form.setFieldValue(
        'oilDcaParams',
        mapDcaForecastParamsToStreamParams(oil.dcaParams)
      );
    }
    if (gas) {
      form.setFieldValue(
        'gasDcaParams',
        mapDcaForecastParamsToStreamParams(gas.dcaParams)
      );
    }
    if (water) {
      form.setFieldValue(
        'waterDcaParams',
        mapDcaForecastParamsToStreamParams(water.dcaParams)
      );
    }

    savedParamsPrefillDoneRef.current = true;
  }, [classifyStreams, form]);

  // -------------------------------------------------------------------------
  // Area config prefill: fallback for streams without saved params. Streams
  // that already have saved params are skipped so their values stay sticky.
  // -------------------------------------------------------------------------
  const lastPrefillKeyRef = useRef('');

  useEffect(() => {
    if (!prefillData) return;

    const key = `${effectiveDcaScenarioId}-${selectedHeaderId}`;
    if (key === lastPrefillKeyRef.current) return;
    lastPrefillKeyRef.current = key;

    const { forecastSettings, productionStreamConfigs } = prefillData;
    const anySaved =
      hasSavedParams.oil || hasSavedParams.gas || hasSavedParams.water;

    if (anySaved) {
      // Only durationOption comes from area config (not in DcaForecastParams).
      form.setFieldValue(
        'forecastDurationOption',
        forecastSettings.durationOption,
        { dontUpdateMeta: true }
      );
    } else {
      form.setFieldValue(
        'qiFormat',
        forecastSettings.qiFormat === 'monthly' ? 'months' : 'days',
        { dontUpdateMeta: true }
      );
      form.setFieldValue('diFormat', forecastSettings.diFormat, {
        dontUpdateMeta: true,
      });
      form.setFieldValue(
        'forecastDurationPeriod',
        forecastSettings.durationPeriod,
        { dontUpdateMeta: true }
      );
      form.setFieldValue(
        'forecastDurationOption',
        forecastSettings.durationOption,
        { dontUpdateMeta: true }
      );
    }

    // Per-stream model params — only for streams without saved params.
    const mapped = mapProductionStreamConfigs(productionStreamConfigs);
    if (!hasSavedParams.oil)
      form.setFieldValue(
        'oilDcaParams',
        mapped.oil ?? { ...DEFAULT_STREAM_DCA_PARAMS },
        { dontUpdateMeta: true }
      );
    if (!hasSavedParams.gas)
      form.setFieldValue(
        'gasDcaParams',
        mapped.gas ?? { ...DEFAULT_STREAM_DCA_PARAMS },
        { dontUpdateMeta: true }
      );
    if (!hasSavedParams.water)
      form.setFieldValue(
        'waterDcaParams',
        mapped.water ?? { ...DEFAULT_STREAM_DCA_PARAMS },
        { dontUpdateMeta: true }
      );

    // Mark parameters from area configs as locked so regression keeps them
    // fixed — mirrors the well-page DCA pattern. Streams with saved params
    // get empty locks (already-fitted values, not constraints).
    const initialLockStates = extractInitialLockStates(productionStreamConfigs);
    if (hasSavedParams.oil) initialLockStates.oil = { dca: {}, rampUp: {} };
    if (hasSavedParams.gas) initialLockStates.gas = { dca: {}, rampUp: {} };
    if (hasSavedParams.water) initialLockStates.water = { dca: {}, rampUp: {} };
    setAllLockStates(initialLockStates);
  }, [
    prefillData,
    effectiveDcaScenarioId,
    selectedHeaderId,
    form,
    setAllLockStates,
    hasSavedParams,
  ]);

  const {
    forecastData,
    forecastOverride,
    isFetching: forecastLoading,
  } = useForecastVolumes({
    uwis,
    typeCurveHeaderDetailsId: selectedHeaderId,
    dcaScenarioId: effectiveDcaScenarioId,
  });

  // ── Saved global state: fall back to the previously-saved forecast so the
  //    modal reopens showing the same overlay/probit the main page displays. ──
  const savedForecastVolumes = useAtomValue(tcDcaForecastVolumesAtom);
  const savedForecastOverride = useAtomValue(tcForecastOverrideAtom);

  // Only use saved data when it belongs to the current TC header — the saved
  // override carries the id it was computed for, and savedForecastVolumes is
  // implicitly scoped to the same TC that produced the override.
  const savedMatchesTc =
    savedForecastOverride?.typeCurveHeaderDetailsId === selectedHeaderId;

  const effectiveForecastData =
    forecastData ?? (savedMatchesTc ? savedForecastVolumes : null);
  const effectiveForecastOverride =
    forecastOverride ?? (savedMatchesTc ? savedForecastOverride : null);

  // ── Atom writers for atomic Save ──
  const setDcaForecastVolumes = useSetAtom(tcDcaForecastVolumesAtom);
  const setGlobalForecastOverride = useSetAtom(tcForecastOverrideAtom);
  const setFitParams = useSetAtom(tcDcaFitParamsAtom);
  const setFitSegments = useSetAtom(tcDcaFitSegmentsAtom);
  const setFitAggregation = useSetAtom(tcDcaFitAggregationAtom);
  const setFitScenarioId = useSetAtom(tcDcaFitScenarioIdAtom);

  // ── Probit override: capture latest result from chart grid, write on save ──
  const setProbitOverride = useSetAtom(tcProbitOverrideAtoms[stream]);
  const latestProbitResultRef = useRef<TypeCurveProbitResponse | null>(null);
  const handleProbitResult = useCallback((data: TypeCurveProbitResponse) => {
    latestProbitResultRef.current = data;
  }, []);

  return (
    <div className={styles.tcDcaModal}>
      <div className={styles.tcDcaModalBackdrop} onClick={onClose} />
      <div className={styles.tcDcaModalContent}>
        <div className={styles.tcDcaModalHeader}>
          <div className={styles.tcDcaModalTitleGroup}>
            <span className={styles.tcDcaModalStreamLabel}>
              {STREAM_LABELS[stream]} stream
            </span>
            <h2 className={styles.tcDcaModalTitle}>Decline Curve Analysis</h2>
          </div>
          <button
            type="button"
            className={styles.tcDcaModalCloseButton}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.tcDcaModalBody}>
          <fieldset className={styles.tcDcaModalSidebar} disabled={isReadOnly}>
            <TcDcaConfigurationPanel
              stream={stream}
              isFitting={isPending}
              onFit={fit}
              regressionResult={regressionResult}
              regressionError={isError ? error : null}
              typeCurveAreaName={typeCurveAreaName}
              activeUniqueTcId={activeUniqueTcId}
              onTypeCurveChange={(uniqueTcId, headerId) => {
                setActiveUniqueTcId(uniqueTcId);
                setSelectedHeaderId(headerId);
              }}
              dcaScenario={modalDcaScenario}
              onDcaScenarioChange={setModalDcaScenario}
              readOnly={isReadOnly}
            />
          </fieldset>
          <div className={styles.tcDcaModalCharts}>
            <TcDcaChartGrid
              stream={stream}
              typeCurveHeaderDetailsId={selectedHeaderId}
              dcaScenarioId={effectiveDcaScenarioId}
              probitDcaScenarioId={dcaScenarioId}
              wells={wells}
              showTypeCurve={showTypeCurve}
              volumeDisplayMode={volumeDisplayMode}
              forecastData={effectiveForecastData}
              showForecast={!!effectiveForecastData}
              productionWells={effectiveProductionWells}
              productionLoading={effectiveProductionLoading}
              typeCurveData={effectiveTypeCurveData}
              aggregationCache={effectiveAggregationCache}
              wellCounts={effectiveWellCounts}
              localForecastOverride={effectiveForecastOverride}
              forecastLoading={forecastLoading}
              onProbitResult={handleProbitResult}
            />
            <div className={styles.tcDcaModalChartControls}>
              <Toggle
                isActive={showTypeCurve}
                handleToggle={setShowTypeCurve}
                label="Display Type Curve"
              />
              <p className={styles.tcDcaModalChartControlsLabel}>
                Volume display
              </p>
              <RadioButtonGroup
                // legendText="Volume display"
                name="tc-dca-volume-display"
                valueSelected={volumeDisplayMode}
                onChange={(value: string | number | undefined) =>
                  setVolumeDisplayMode(
                    (value as 'monthly' | 'daily') ?? 'monthly'
                  )
                }
              >
                <RadioButton
                  labelText="Monthly"
                  value="monthly"
                  id="tc-dca-vol-monthly"
                />
                <RadioButton
                  labelText="Daily"
                  value="daily"
                  id="tc-dca-vol-daily"
                />
              </RadioButtonGroup>
            </div>
          </div>
        </div>
        <div className={styles.tcDcaModalFooter}>
          <Button
            kind="secondary"
            className={styles.cancelButton}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            kind="primary"
            disabled={!isFormDirty || !isFormValid || isReadOnly}
            onClick={() => {
              // ── Atomic commit: push all deferred local state to global atoms ──

              // Forecast volumes + probit override
              if (effectiveForecastData) {
                setDcaForecastVolumes(effectiveForecastData);
              }
              if (effectiveForecastOverride) {
                // Merge: only update the active stream's EUR/dcaParams so
                // non-active streams' probit charts don't re-fetch.
                setGlobalForecastOverride((prev) => {
                  const nxt = effectiveForecastOverride;
                  return {
                    typeCurveHeaderDetailsId: nxt.typeCurveHeaderDetailsId,
                    eur: nxt.eur
                      ? ({
                          ...(prev?.eur ?? {}),
                          [stream]: nxt.eur[stream],
                        } as typeof nxt.eur)
                      : (prev?.eur ?? null),
                    eurRatios: nxt.eurRatios ?? prev?.eurRatios ?? null,
                    cumulatives:
                      nxt.cumulatives.length > 0
                        ? nxt.cumulatives
                        : (prev?.cumulatives ?? []),
                    dcaParams: {
                      ...(prev?.dcaParams ?? {}),
                      [stream]: nxt.dcaParams?.[stream] ?? null,
                    },
                  };
                });
              }

              // Always commit the active stream's DCA params from the form.
              // If Fit was run, the form already contains the fitted values.
              const formDcaParams = buildFormDcaParams(
                form.state.values,
                stream
              );
              setFitParams((prev) => ({ ...prev, [stream]: formDcaParams }));
              setFitAggregation((prev) => ({
                ...prev,
                [stream]: toApiAggregation(aggregation),
              }));
              setFitScenarioId((prev) => ({
                ...prev,
                [stream]: effectiveDcaScenarioId,
              }));

              // Fit results (segments, aggregation, scenario IDs) merge on top.
              // Params do NOT: Fit writes its results into the form, so the form
              // is either equal to the fit or ahead of it because the user edited
              // afterwards. Letting fitResults win here discarded those edits
              // (and nulled ramp-up the fit did not return). Other streams still
              // come from the fit — only the active stream is editable here.
              if (Object.keys(fitResults.params).length > 0) {
                setFitParams((prev) => ({
                  ...prev,
                  ...fitResults.params,
                  [stream]: formDcaParams,
                }));
              }
              if (Object.keys(fitResults.segments).length > 0) {
                setFitSegments((prev) => ({ ...prev, ...fitResults.segments }));
              }
              if (Object.keys(fitResults.aggregations).length > 0) {
                setFitAggregation((prev) => ({
                  ...prev,
                  ...fitResults.aggregations,
                }));
              }
              if (Object.keys(fitResults.scenarioIds).length > 0) {
                setFitScenarioId((prev) => ({
                  ...prev,
                  ...fitResults.scenarioIds,
                }));
              }

              // Copy probit result to shared override atom so main page shows
              // the golden "Type Curve" annotation line without re-fetching.
              if (latestProbitResultRef.current) {
                setProbitOverride(latestProbitResultRef.current);
              }

              wasSavedRef.current = true;
              onClose();
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
